import React, { useMemo, useState } from 'react';
import {
  Image,
  Keyboard,
  Pressable,
  ScrollView,
  StyleSheet,
  TextInput,
  View,
} from 'react-native';
import Icon from '../components/ui/Icon';
import RotatingHint from '../components/ui/RotatingHint';
import Text from '../components/ui/Text';
import { cardDuration, cardPrice, cardWasPrice } from '../data/hourly';
import {
  Category,
  CategoryKey,
  Service,
} from '../data/services';
import { hintsFrom } from '../services/catalogue';
import {
  colors,
  hairline,
  inputText,
  radii,
  screenPadding,
  spacing,
  useResponsive,
} from '../theme';

/** Whole rupees, grouped - `Intl` is not reliably present on Android JSC. */
const rupees = (value: number) =>
  '₹' + String(value).replace(/\B(?=(\d{3})+(?!\d))/g, ',');

type Props = {
  /** The whole catalogue. Filtering happens here, not at the call site. */
  services: Service[];
  /**
   * The categories to group and filter by.
   *
   * Passed in rather than imported. This screen used to read the seed's own
   * eight categories and its subcategories directly, which broke in two ways
   * the moment the catalogue came from the platform: the chips listed
   * categories the customer cannot see, and - worse - each chip carried the
   * SEED's services for that category. Every one of those is tappable and
   * addable to the basket, at a price nobody set, for a service the server has
   * never heard of.
   */
  categories: Category[];
  categoryFor: (key: CategoryKey) => Category;
  /** The services under a category, from whoever owns the catalogue. */
  servicesIn: (key: CategoryKey) => Service[];
  onBack: () => void;
  onOpenService: (service: Service) => void;
};

/**
 * Search, as its own screen.
 *
 * The field on Home used to open the catalogue sheet, which answered a
 * different question - someone tapping a box that says "Search for carpentry"
 * wants to type, not to be handed a list of categories to browse.
 *
 * Opens to the field and nothing else, keyboard up. It briefly opened onto a
 * grid of all forty-eight services, which read as a page to scroll past
 * rather than a shortcut - someone who taps a search field has already decided
 * to type, and Home is where browsing belongs.
 *
 * Typing brings back two things: a row of the shelves the query touches, and
 * the matching services as text beneath them.
 */
function SearchScreen({
  services,
  categories,
  categoryFor,
  servicesIn,
  onBack,
  onOpenService,
}: Props) {
  const resp = useResponsive();
  const [query, setQuery] = useState('');

  /*
   * The hints are the catalogue's own service names, not the bundled
   * `searchHints`.
   *
   * A hint is a promise that typing it finds something, and the compiled list
   * - "deep cleaning", "AC service" - was written for a catalogue this app no
   * longer sells. Every one of them now finds nothing, which is a worse first
   * impression than an empty field: it reads as search being broken rather
   * than as that service not existing.
   */
  const hints = useMemo(() => hintsFrom(services), [services]);

  const needle = query.trim().toLowerCase();

  /*
   * Name, blurb and category label are all searched, so "cleaning" finds the
   * services under Cleaning even though none of them repeats the word, and
   * "tap" finds the mixer fitting by its description.
   */
  const results = useMemo(() => {
    if (!needle) {
      return services;
    }
    return services.filter(service => {
      const haystack = `${service.name} ${service.blurb} ${
        categoryFor(service.category).label
      }`.toLowerCase();
      return haystack.includes(needle);
    });
  }, [categoryFor, needle, services]);

  /*
   * The shelves a query touches, as filter chips.
   *
   * A shelf qualifies two ways: its own name matches, or it holds a service
   * that matches. The second is what makes the chips useful - typing "deep"
   * finds services scattered across three subcategories, and the chips are how
   * you see that and pick one.
   *
   * Each chip carries the shelf's *whole* contents rather than only the
   * matches. Someone who has narrowed to "Kitchen cleaning" has stopped
   * searching and started browsing, and hiding the rest of that shelf because
   * it did not contain their typo is unhelpful. It is also what rescues a
   * query like "salon", where the category name matches and no service does.
   */
  const shelves = useMemo(() => {
    if (!needle) {
      return [];
    }

    const out: Array<{ key: string; label: string; services: Service[] }> = [];
    const add = (category: Category) => {
      const items = servicesIn(category.key);
      if (items.length && !out.some(shelf => shelf.key === category.key)) {
        out.push({ key: category.key, label: category.label, services: items });
      }
    };

    // Named outright - "salon" matches the category even if no service does.
    categories.forEach(category => {
      if (category.label.toLowerCase().includes(needle)) {
        add(category);
      }
    });

    // Reached through a service that matched.
    results.forEach(service => add(categoryFor(service.category)));

    return out;
  }, [categories, categoryFor, needle, results, servicesIn]);

  /*
   * Which chip is active. `all` is the default and cannot go stale; a shelf
   * key can, once the query changes and that shelf stops qualifying, so the
   * lookup below falls back rather than rendering nothing.
   */
  const [shelf, setShelf] = useState('all');
  const active = shelves.find(item => item.key === shelf);

  /** The listing: matches grouped by shelf, or one shelf in full. */
  const groups = useMemo(() => {
    if (active) {
      return [
        { key: active.key, label: active.label, services: active.services },
      ];
    }

    const out: Array<{ key: string; label: string; services: Service[] }> = [];
    results.forEach(service => {
      /* Grouped by category, which both catalogues have. Subcategories are a
         grouping that exists only in the bundled one. */
      const key = service.category;
      const label = categoryFor(key).label;
      const existing = out.find(group => group.key === key);
      if (existing) {
        existing.services.push(service);
      } else {
        out.push({ key, label, services: [service] });
      }
    });
    return out;
  }, [active, categoryFor, results]);

  return (
    <View style={styles.root}>
      {/* Back sits inside the field rather than beside it, so the field is the
          whole width of the bar - it is the only thing this screen is for. */}
      <View
        style={[
          styles.bar,
          /* Above the scroller, so it centres on the page's column by hand.
             Zero on a phone. */
          { paddingHorizontal: screenPadding + resp.sideInset },
        ]}
      >
        <View style={styles.field}>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Back"
            onPress={onBack}
            hitSlop={10}
            style={({ pressed }) => (pressed ? styles.pressed : null)}
          >
            <Icon name="chevronLeft" size={20} color={colors.ink} />
          </Pressable>

          {/*
            The hint is a sibling laid over the field, not the input's own
            `placeholder` - a placeholder is a static string and cannot cycle.
            `pointerEvents="none"` so it never intercepts a tap meant for the
            field underneath it, and it is dropped entirely once anything is
            typed rather than hidden, so its timer stops with it.
          */}
          <View style={styles.inputWrap}>
            <TextInput
              value={query}
              onChangeText={setQuery}
              autoFocus
              returnKeyType="search"
              onSubmitEditing={Keyboard.dismiss}
              style={styles.input}
              accessibilityLabel="Search for a service"
            />

            {query ? null : (
              <View style={styles.hint} pointerEvents="none">
                <RotatingHint hints={hints} />
              </View>
            )}
          </View>

          {/* Only once there is something to clear - an always-on cross on an
              empty field reads as a control that does nothing. */}
          {query ? (
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Clear search"
              onPress={() => setQuery('')}
              hitSlop={10}
            >
              <Icon
                name="plus"
                size={15}
                color={colors.inkMuted}
                strokeWidth={2.2}
                style={styles.clearGlyph}
              />
            </Pressable>
          ) : null}
        </View>
      </View>

      <ScrollView
        /* Zero on a phone; a centred column on a tablet, where a full-width
           list of results is a name at one edge and a price at the other. */
        contentContainerStyle={[styles.content, resp.column]}
        showsVerticalScrollIndicator={false}
        // Lets a tap land on a tile directly instead of being eaten by the
        // keyboard dismissing first.
        keyboardShouldPersistTaps="handled"
        onScrollBeginDrag={Keyboard.dismiss}
      >
        {/*
          A prompt, not a catalogue.

          The wall of every service used to sit here, and it was answering a
          question nobody on this screen had asked: someone who tapped a search
          field intends to type, and forty-eight photographs between them and
          the keyboard is a page to scroll past rather than a shortcut. What is
          left says what the field will accept, and cycles a live example so
          the screen is not simply empty while the keyboard comes up.
        */}
        {!needle ? (
          <View style={styles.prompt}>
            <Text variant="title" align="center">
              What do you need done?
            </Text>
            <Text variant="bodySm" color={colors.inkMuted} align="center">
              Search by service, or by the trade it belongs to.
            </Text>

            <View style={styles.promptHint}>
              <RotatingHint
                hints={hints}
                prefix="Try "
                style={styles.hintShrink}
              />
            </View>
          </View>
        ) : null}

        {/* ------------------------------------ typed: the shelves, then the list */}
        {needle && shelves.length ? (
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={styles.shelfRail}
            keyboardShouldPersistTaps="handled"
          >
            {[
              { key: 'all', label: 'All', services: [] as Service[] },
              ...shelves,
            ].map(item => {
              const on = item.key === (active ? active.key : 'all');
              // The shelf's first photograph stands for it; there is no
              // artwork of a shelf, only of the work on it.
              const art = item.services[0]?.image;

              return (
                <Pressable
                  key={item.key}
                  accessibilityRole="button"
                  accessibilityState={{ selected: on }}
                  accessibilityLabel={item.label}
                  onPress={() => setShelf(item.key)}
                  style={({ pressed }) => [
                    styles.shelf,
                    pressed ? styles.pressed : null,
                  ]}
                >
                  <View
                    style={[styles.shelfArt, on ? styles.shelfArtOn : null]}
                  >
                    {art ? (
                      <Image
                        source={art}
                        style={styles.photo}
                        resizeMode="cover"
                      />
                    ) : (
                      <Icon
                        name="grid"
                        size={22}
                        color={on ? colors.primary : colors.inkMuted}
                      />
                    )}
                  </View>

                  <Text
                    variant="caption"
                    align="center"
                    numberOfLines={2}
                    color={on ? colors.primary : colors.inkBody}
                  >
                    {item.label}
                  </Text>
                </Pressable>
              );
            })}
          </ScrollView>
        ) : null}

        {needle
          ? groups.map(group => (
              <View key={group.key} style={styles.group}>
                <Text
                  variant="eyebrow"
                  color={colors.inkMuted}
                  style={styles.heading}
                >
                  {group.label.toUpperCase()}
                </Text>

                {group.services.map(service => (
                  <Pressable
                    key={service.key}
                    accessibilityRole="button"
                    accessibilityLabel={`${service.name}, ${rupees(
                      service.price,
                    )}, ${service.minutes} minutes`}
                    onPress={() => onOpenService(service)}
                    style={({ pressed }) => [
                      styles.row,
                      pressed ? styles.pressed : null,
                    ]}
                  >
                    <View style={styles.rowText}>
                      <Text variant="subtitle" numberOfLines={1}>
                        {service.name}
                      </Text>
                      <Text
                        variant="caption"
                        color={colors.inkMuted}
                        numberOfLines={1}
                      >
                        {/* Nested `Text` so the strike lands on this figure
                            alone - the line is one caption and the duration
                            and category must not be crossed out with it. */}
                        {cardWasPrice(service, rupees) ? (
                          <Text style={styles.was}>
                            {cardWasPrice(service, rupees)}{' '}
                          </Text>
                        ) : null}
                        {cardPrice(service, rupees)} ·{' '}
                        {cardDuration(service)} ·{' '}
                        {categoryFor(service.category).label}
                      </Text>
                    </View>

                    {/* On the right, where it terminates the row rather than
                        leading it - the name is what is being scanned down
                        this list, and a column of pictures on the left pushes
                        every one of them in. */}
                    <View style={styles.rowArt}>
                      {service.image ? (
                        <Image
                          source={service.image}
                          style={styles.photo}
                          resizeMode="cover"
                        />
                      ) : (
                        <Icon
                          name={categoryFor(service.category).icon}
                          size={20}
                          color={colors.primary}
                        />
                      )}
                    </View>
                  </Pressable>
                ))}
              </View>
            ))
          : null}

        {needle && !groups.length ? (
          <View style={styles.empty}>
            <Text variant="subtitle" align="center">
              Nothing matches "{query.trim()}"
            </Text>
            <Text variant="bodySm" color={colors.inkMuted} align="center">
              Try a trade - cleaning, plumbing, salon - or the name of the job.
            </Text>
          </View>
        ) : null}
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  /* The strike itself. Nothing else in this app crosses text out. */
  was: {
    textDecorationLine: 'line-through',
  },
  root: {
    flex: 1,
    backgroundColor: colors.background,
  },
  bar: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    paddingHorizontal: screenPadding,
    paddingTop: spacing.sm,
    paddingBottom: spacing.md,
  },
  field: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    /* 52, not 44. The field is the whole point of this screen and the only
       thing on it before anything is typed - at 44 it read as a header
       affordance rather than as the thing to use. */
    height: 52,
    paddingHorizontal: spacing.md,
    borderRadius: radii.pill,
    backgroundColor: colors.surface,
    ...hairline,
  },
  inputWrap: {
    flex: 1,
    justifyContent: 'center',
  },
  input: {
    ...inputText,
  },
  /* Spelled out rather than `absoluteFill`, which this React Native version
     does not expose on the style type. */
  hint: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: 'center',
  },
  // A rotated plus - the icon set has no cross glyph.
  clearGlyph: {
    transform: [{ rotate: '45deg' }],
  },

  content: {
    paddingBottom: spacing.xxl,
  },
  heading: {
    paddingHorizontal: screenPadding,
    paddingBottom: spacing.md,
  },
  grid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.md,
    paddingHorizontal: screenPadding,
  },
  tile: {
    alignItems: 'center',
    gap: spacing.sm,
  },
  art: {
    borderRadius: radii.md,
    overflow: 'hidden',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.surfaceAccent,
  },
  photo: {
    width: '100%',
    height: '100%',
  },
  prompt: {
    alignItems: 'center',
    gap: spacing.xs,
    paddingHorizontal: screenPadding,
    paddingTop: spacing.md,
  },
  /* The rotator lays its two halves out in a row and takes the width it is
     given, so it needs a centred box of its own rather than `align="center"`
     on the text inside it. */
  /* The rotator fills its parent by default, which is right inside a field and
     wrong inside a pill that should hug its words. */
  hintShrink: {
    flex: 0,
  },
  promptHint: {
    flexDirection: 'row',
    justifyContent: 'center',
    marginTop: spacing.lg,
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.sm,
    borderRadius: radii.pill,
    backgroundColor: colors.surfaceAccent,
  },

  /* One row, scrolling sideways. Picture over label, like the category rail -
     a shelf is easier to recognise by the work on it than by its name. */
  shelfRail: {
    gap: spacing.md,
    paddingHorizontal: screenPadding,
    paddingBottom: spacing.md,
  },
  shelf: {
    width: 68,
    alignItems: 'center',
    gap: spacing.xs,
  },
  shelfArt: {
    width: 60,
    height: 60,
    borderRadius: radii.md,
    overflow: 'hidden',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.surfaceAccent,
    ...hairline,
  },
  /* A ring rather than a fill: the picture is the tile, and tinting over it
     would hide the one thing that identifies the shelf. */
  shelfArtOn: {
    borderWidth: 2,
    borderColor: colors.primary,
  },

  group: {
    paddingTop: spacing.md,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    paddingHorizontal: screenPadding,
    paddingVertical: spacing.md,
    borderTopWidth: hairline.borderWidth,
    borderTopColor: hairline.borderColor,
  },
  rowText: {
    flex: 1,
  },
  rowArt: {
    width: 56,
    height: 56,
    borderRadius: radii.sm,
    overflow: 'hidden',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.surfaceAccent,
  },
  empty: {
    alignItems: 'center',
    gap: spacing.xs,
    paddingHorizontal: screenPadding,
    paddingVertical: spacing.xxl,
  },
  pressed: {
    opacity: 0.6,
  },
});

export default SearchScreen;
